// Every take upload runs `uploadTake` (wiki/feature-upload-reliability.md).

import { api, ApiError, isAccountMoving } from './client';
import type {
  UploadReason,
  FrameworkLens,
  Practice,
  PronunciationAccent,
  SaveReason,
  IssueUploadUrlResponse,
  IssueUploadUrlCommand,
  IssueRecordingUploadResponse,
  SignRecordingUploadPartsRequest,
  RecordingUploadPartsResponse,
  CompleteRecordingUploadRequest,
  RecordingUploadCompletionResponse,
  CompleteThumbnailUploadCommand,
  RequestRecordingReviewCommand,
  ThinkingLevel,
} from '@voicedojo/backend-types';
import {
  AllowedRecordingContentTypes,
  AiReviewId,
  MaxUploadPartsPerSign,
  PracticeMode,
  RecordingsKeys,
  RecordingUploadState,
} from '@voicedojo/backend-types';
import { t } from '$lib/i18n';
import type { LiveConvoTake } from '$lib/liveConvo/liveConvo';
import type { ReviewFrame, ReviewModelKey } from './reviewQuality';
import { saveRecording, bareRecordingId } from './recordings';
import { getRecording } from './recordingDetail';
import { setRecordingNotes } from './progress';
import { readModelsHub } from './readModelsHub';
import { isAuthenticated } from '$lib/stores/auth';
import { get } from 'svelte/store';
import {
  localRecordingCache,
  type LocalTakeMeta,
  type PendingUpload,
} from '$lib/utils/recording/localRecordingCache';
import {
  autoCaptureThumbnail,
  THUMBNAIL_CONTENT_TYPE,
} from '$lib/utils/recording/captureThumbnail';
import { videoQualityStore } from '$lib/stores/videoQualityStore';

async function clearPending(recordingId: string): Promise<void> {
  try {
    await localRecordingCache.removePending(recordingId);
  } catch {
    // best-effort
  }
}

// Only the server's confirmation may drop the local copy — never a resolved PUT.
async function resolveConfirmedUpload(recordingId: string, takeId: string): Promise<void> {
  await clearPending(recordingId);
  try {
    await localRecordingCache.remove(takeId);
  } catch {
    // the 7-day expiry reclaims it
  }
}

// Only offline mode mints `offline:` take ids.
export function isOfflineTake(id: string | null | undefined): boolean {
  return id?.startsWith('offline:') === true;
}

export function splitPracticeText(text: string | undefined): { title: string; body: string } {
  if (!text) return { title: '', body: '' };
  const sep = text.indexOf('\n\n');
  if (sep === -1) return { title: '', body: text };
  return { title: text.slice(0, sep), body: text.slice(sep + 2) };
}

export type MaterializeOverride = {
  practice?: Practice;
  name?: string;
  // The live take's facts: its cache write may not have landed.
  takeMeta?: LocalTakeMeta;
  seriesId?: string;
  optionalContext?: string;
  seriesDocumentRevision?: number;
  deliveredPlanPoints?: number[];
  seriesPlanId?: string;
  speakerPassage?: string;
  liveConvo?: LiveConvoTake;
  reviewId?: string;
  review?: ReviewQualityOptions;
};

export async function materializeRecording(
  takeId: string,
  reason: SaveReason,
  override: MaterializeOverride = {},
): Promise<string> {
  const id = bareRecordingId(takeId);
  const { takeMeta } = override;
  const cached =
    !takeMeta && localRecordingCache.isAvailable() ? await localRecordingCache.getMeta(takeId) : null;

  // No default: a guessed practice writes a lie into the user's history.
  const practice = override.practice ?? cached?.practiceKind;
  if (practice === undefined) {
    throw new ApiError(422, t(RecordingsKeys.Errors.UnknownPractice));
  }

  const endedAt = takeMeta?.endedAt ?? cached?.endedAt ?? new Date().toISOString();
  const startedAt = takeMeta?.startedAt ?? cached?.startedAt ?? endedAt;
  const durationSeconds = takeMeta?.durationSeconds ?? cached?.durationSeconds ?? null;
  const plannedDurationMinutes = Math.max(1, Math.ceil((durationSeconds ?? 0) / 60));
  const { title, body } = splitPracticeText(takeMeta?.practiceText ?? cached?.practiceText);

  // The cache writes plan id and points as a pair.
  const seriesId = override.seriesId ?? cached?.seriesId;
  const seriesPlanId = override.seriesPlanId ?? cached?.seriesPlanId;
  const deliveredPlanPoints = override.deliveredPlanPoints ?? cached?.deliveredPlanPoints;
  const seriesDocumentRevision =
    override.seriesDocumentRevision ?? cached?.seriesDocumentRevision;
  const speakerPassage = override.speakerPassage ?? cached?.speakerPassage;
  const liveConvo = override.liveConvo ?? cached?.liveConvo;

  await saveRecording({
    id,
    practice,
    name: override.name ?? takeMeta?.sessionName ?? cached?.sessionName ?? '',
    plannedDurationMinutes,
    startedAt,
    endedAt,
    content: { kind: 'Story', title, body },
    cuePlan: (takeMeta?.cues ?? cached?.cues ?? []).map(({ offsetSeconds, message }) => ({
      offsetSeconds,
      message,
    })),
    reason,
    ...(seriesId ? { seriesId } : {}),
    ...(override.optionalContext ? { optionalContext: override.optionalContext } : {}),
    ...(seriesDocumentRevision !== undefined ? { seriesDocumentRevision } : {}),
    ...(deliveredPlanPoints ? { deliveredPlanPoints } : {}),
    ...(seriesPlanId ? { seriesPlanId } : {}),
    ...(speakerPassage ? { speakerPassage } : {}),
    ...(liveConvo
      ? {
          mode: liveConvo.mode ?? PracticeMode.LiveConvo,
          liveConvoModelKey: liveConvo.modelKey,
          coachFeedback: liveConvo.coachFeedback,
          feedbackStartSeconds: liveConvo.feedbackStartSeconds,
          ...(liveConvo.roster ? { livePitchRoster: liveConvo.roster } : {}),
        }
      : {}),
    durationSeconds,
    // Quality is locked while recording, so the live tier is the take's tier.
    recordedQuality: videoQualityStore.snapshot().tier,
    ...(override.reviewId ? { reviewId: override.reviewId } : {}),
    ...(override.review?.modelKey ? { reviewModelKey: override.review.modelKey } : {}),
    ...(override.review?.frame ? { reviewFrame: override.review.frame } : {}),
    ...(override.review?.frameworks && override.review.frameworks.length > 0
      ? { reviewFrameworks: override.review.frameworks }
      : {}),
    ...(override.review?.pronunciation ? { reviewPronunciation: override.review.pronunciation } : {}),
    ...(override.review?.effort ? { reviewEffort: override.review.effort } : {}),
  });
  // Cache, not takeMeta: the Details page in another tab may have edited the note.
  const notes = (cached ?? (await localRecordingCache.getMeta(takeId).catch(() => null)))?.notes;
  if (notes) await setRecordingNotes(id, notes);
  return id;
}

export type ReviewQualityOptions = {
  modelKey?: ReviewModelKey | null;
  frame?: ReviewFrame | null;
  frameworks?: FrameworkLens[] | null;
  pronunciation?: PronunciationAccent | null;
  effort?: ThinkingLevel | null;
};

// The signed URL is bound to the bare MIME, so upload-url and the PUT must agree on it.
export function coerceUploadContentType(b: Blob | null): string {
  if (!b) return '';
  const fromType = bareRecordingMime(b.type);
  if (fromType) return fromType;
  const name = (b as File).name?.toLowerCase?.() ?? '';
  const byExtension = Object.entries(AllowedRecordingContentTypes).find(([, ext]) =>
    name.endsWith(`.${ext}`),
  );
  return byExtension?.[0] ?? '';
}

function bareRecordingMime(value: string | null | undefined): string {
  const t = (value || '').split(';')[0].trim().toLowerCase();
  return Object.hasOwn(AllowedRecordingContentTypes, t) ? t : '';
}

export type UploadProgress = (percent: number) => void;

// XHR, not fetch: only XHR reports upload progress. Resolves with the ETag (a multipart part needs it).
export function putToSignedUrl(
  uploadUrl: string,
  blob: Blob,
  contentType?: string,
  onProgress?: UploadProgress,
  signal?: AbortSignal,
): Promise<string | null> {
  return new Promise<string | null>((resolve, reject) => {
    if (signal?.aborted) return reject(signal.reason);
    const xhr = new XMLHttpRequest();
    signal?.addEventListener('abort', () => {
      xhr.abort();
      reject(signal.reason);
    });
    xhr.open('PUT', uploadUrl, true);
    if (contentType) xhr.setRequestHeader('Content-Type', contentType);

    if (onProgress) {
      onProgress(0);
      xhr.upload.onprogress = (e: ProgressEvent): void => {
        if (e.lengthComputable && e.total > 0) {
          onProgress(Math.min(100, Math.round((e.loaded / e.total) * 100)));
        }
      };
    }

    xhr.onload = (): void => {
      if (xhr.status >= 200 && xhr.status < 300) {
        if (onProgress) onProgress(100);
        resolve(xhr.getResponseHeader('ETag'));
      } else {
        reject(new ApiError(xhr.status, 'Object storage rejected the upload.'));
      }
    };
    const failTransport = (): void =>
      reject(new ApiError(0, 'Network error — please check your connection and try again.'));
    xhr.onerror = failTransport;
    xhr.ontimeout = failTransport;

    xhr.send(blob);
  });
}

export type UploadServerState = 'unknown' | 'started';

// No deadline: the R2 → queue → webhook → push path has no bound of its own.
// False only for a guest, who has no read channel.
function awaitUploadConfirmed(
  recordingId: string,
  signal: AbortSignal,
  onServerState?: (state: UploadServerState) => void,
): Promise<boolean> {
  if (!get(isAuthenticated)) return Promise.resolve(false);
  const id = bareRecordingId(recordingId).toLowerCase();

  return new Promise<boolean>((resolve, reject) => {
    let settled = false;

    const read = async (): Promise<void> => {
      if (settled) return;
      let recording: Awaited<ReturnType<typeof getRecording>> | null = null;
      try {
        recording = await getRecording(id);
      } catch {
        // the next push asks again
      }
      if (settled || !recording) return;
      if (recording.recordingUploadedAt) {
        settled = true;
        unsubscribe();
        unregisterResync();
        resolve(true);
        return;
      }
      onServerState?.(recording.recordingUploadStartedAt ? 'started' : 'unknown');
    };

    const unsubscribe = readModelsHub.subscribe('Recording', (change) => {
      if (change.entityId?.toLowerCase() === id) void read();
    });
    const unregisterResync = readModelsHub.onResync(() => void read());
    signal.addEventListener('abort', () => {
      if (settled) return;
      settled = true;
      unsubscribe();
      unregisterResync();
      reject(signal.reason);
    });
    void read();
  });
}

export type TakeUploadOptions = MaterializeOverride & {
  reason: UploadReason;
  onProgress?: UploadProgress;
  onServerState?: (state: UploadServerState) => void;
};

export type TakeUpload = {
  recordingId: string;
  // upload-url 409: the bytes were stored before this call, so nothing was sent.
  alreadyStored: boolean;
  // The server verified the object and recorded the upload: the take is saved.
  stored: boolean;
  confirmed: Promise<boolean>;
};

// One sender per recording in this tab until the server confirms it: the resume pass must
// never start a second upload of a take this tab is still sending.
const sendingIds = new Set<string>();

// Keep-upload callers pre-gate the quota: the server refuses a complete over it.
export async function uploadTake(
  takeId: string,
  blob: Blob,
  rawContentType: string,
  { reason, onProgress, onServerState, ...override }: TakeUploadOptions,
): Promise<TakeUpload> {
  const contentType = coerceUploadContentType(blob) || bareRecordingMime(rawContentType) || rawContentType;
  const sendingId = bareRecordingId(takeId);
  const upload = track(takeId, false);
  sendingIds.add(sendingId);
  try {
    const entry: PendingUpload = { recordingId: sendingId, takeId, reason, contentType, sizeBytes: blob.size, createdAt: Date.now() };
    await writing(upload, saveThroughMove(entry, override, upload));
    upload.controller.signal.throwIfAborted();
    await journal(entry);
    const sent = await sendTake(entry, blob, upload, onProgress, onServerState);
    const confirmed = sent.confirmed.finally(() => sendingIds.delete(sendingId));
    confirmed.catch(() => undefined);
    return { ...sent, confirmed };
  } catch (e) {
    sendingIds.delete(sendingId);
    throw e;
  }
}

// Keyed by recording id; an entry outlives its upload. `created`: the server holds the recording.
type Upload = { controller: AbortController; created: boolean; write: Promise<unknown> };
const uploads = new Map<string, Upload>();

function track(takeId: string, created: boolean): Upload {
  const upload = { controller: new AbortController(), created, write: Promise.resolve() };
  uploads.set(bareRecordingId(takeId), upload);
  return upload;
}

// A write to the recording is awaited, never aborted, so a Discard always lands after it.
function writing<T>(upload: Upload, write: Promise<T>): Promise<T> {
  upload.write = write.catch(() => undefined);
  return write;
}

// Discard is the way out, so it stops the upload here; R2's lifecycle rule reaps the parts.
// True when this tab created the take's recording, so the server holds one to discard.
export async function stopUpload(takeId: string): Promise<boolean> {
  const id = bareRecordingId(takeId);
  const upload = uploads.get(id);
  uploads.delete(id);
  upload?.controller.abort();
  await clearPending(id);
  await upload?.write;
  return upload?.created ?? false;
}

export const UPLOAD_CONCURRENCY = 4;
const ATTEMPTS = 5;
const RETRY_BASE_MS = 500;
// R2 answers an expired URL without CORS headers, so it reads as a network error: re-sign before then.
const URL_EXPIRY_MARGIN_MS = 60_000;

// Storage no longer knows the upload id (completed, aborted or reaped): start a fresh one.
class UploadGone extends Error {}

async function journal(entry: PendingUpload): Promise<void> {
  try {
    await localRecordingCache.putPending(entry);
  } catch {
    // best-effort: only crash-resume is lost
  }
}

function wait(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(resolve, ms);
    signal.addEventListener(
      'abort',
      () => {
        clearTimeout(timer);
        reject(signal.reason);
      },
      { once: true },
    );
  });
}

// A home move refuses writes for a few minutes, and the take is safe on this device meanwhile, so
// the wait has no attempt limit. It ends when the move does, or with a Discard: the flip answers
// 421 and the reload crosses to the new cell, where the journal resumes.
const MOVE_RETRY_SECONDS = 30;

async function throughMove<T>(signal: AbortSignal, attempt: () => Promise<T>, onRefused?: () => Promise<void>): Promise<T> {
  for (;;) {
    signal.throwIfAborted();
    try {
      return await attempt();
    } catch (e) {
      if (!isAccountMoving(e)) throw e;
      await onRefused?.();
      await wait((e.retryAfterSeconds ?? MOVE_RETRY_SECONDS) * 1000, signal);
    }
  }
}

// Journalled once the freeze refuses it, with all it needs to be made after the reload at the flip.
// An ordinary failure takes that back: then the save failed in front of the speaker, as ever.
async function saveThroughMove(entry: PendingUpload, override: MaterializeOverride, upload: Upload): Promise<void> {
  let journalled = false;
  try {
    await throughMove(
      upload.controller.signal,
      () => materializeRecording(entry.takeId, entry.reason === 'Review' ? 'Review' : 'Library', override),
      async () => {
        if (journalled) return;
        journalled = true;
        await journal({ ...entry, saveFirst: override });
      },
    );
    upload.created = true;
  } catch (e) {
    if (journalled) await clearPending(entry.recordingId);
    throw e;
  }
}

async function withRetries<T>(signal: AbortSignal, attempt: () => Promise<T>): Promise<T> {
  for (let n = 1; ; n++) {
    signal.throwIfAborted();
    try {
      return await throughMove(signal, attempt);
    } catch (e) {
      const transient = e instanceof ApiError && (e.status === 0 || e.status >= 500);
      if (!transient || n >= ATTEMPTS) throw e;
      await new Promise((resolve) => setTimeout(resolve, RETRY_BASE_MS * 2 ** (n - 1)));
    }
  }
}

async function sendParts(
  id: string,
  entry: PendingUpload,
  blob: Blob,
  signal: AbortSignal,
  onProgress?: UploadProgress,
): Promise<string[]> {
  const upload = entry.multipart!;
  const size = upload.partSizeBytes;
  const count = Math.max(1, Math.ceil(blob.size / size));
  const sizeOf = (n: number): number => Math.max(0, Math.min(size, blob.size - (n - 1) * size));
  const queue = Array.from({ length: count }, (_, i) => i + 1).filter((n) => !upload.etags[n]);
  const urls = new Map<number, { url: string; expiresAt: number }>();
  const inFlight = new Map<number, number>();

  const report = (): void => {
    if (!onProgress) return;
    let sent = 0;
    for (let n = 1; n <= count; n++) sent += upload.etags[n] ? sizeOf(n) : (inFlight.get(n) ?? 0);
    onProgress(blob.size > 0 ? Math.round((sent / blob.size) * 100) : 100);
  };

  // One sign request at a time: concurrent workers share its batch.
  let signing: Promise<void> | null = null;
  const sign = async (partNumbers: number[]): Promise<void> => {
    const request: SignRecordingUploadPartsRequest = {
      objectKey: upload.objectKey,
      uploadId: upload.uploadId,
      partNumbers,
    };
    const batch = await api.post<RecordingUploadPartsResponse>(`/recordings/${id}/upload-parts`, request, {
      requiresAuth: true,
      signal,
    });
    const expiresAt = Date.parse(batch.expiresAt);
    for (const part of batch.parts) urls.set(part.partNumber, { url: part.url, expiresAt });
  };
  // A URL is used once, so a retried part is always re-signed. A just-signed URL skips the
  // expiry check: a skewed local clock must not re-sign forever.
  const take = (n: number): { url: string; expiresAt: number } | undefined => {
    const url = urls.get(n);
    urls.delete(n);
    return url;
  };
  const urlFor = async (n: number): Promise<string> => {
    const cached = take(n);
    if (cached && cached.expiresAt - Date.now() > URL_EXPIRY_MARGIN_MS) return cached.url;
    for (;;) {
      signing ??= sign([n, ...queue].slice(0, MaxUploadPartsPerSign)).finally(() => {
        signing = null;
      });
      await signing;
      const fresh = take(n);
      if (fresh) return fresh.url;
    }
  };

  const sendPart = async (n: number): Promise<void> => {
    const body = blob.slice((n - 1) * size, n * size);
    upload.etags[n] = await withRetries(signal, async () => {
      const url = await urlFor(n);
      const etag = await putToSignedUrl(url, body, undefined, (percent) => {
        inFlight.set(n, (percent / 100) * body.size);
        report();
      }, signal).catch((e: unknown) => {
        throw e instanceof ApiError && e.status === 404 ? new UploadGone() : e;
      });
      if (!etag) throw new ApiError(0, 'Object storage returned no ETag for the part.');
      return etag;
    });
    inFlight.delete(n);
    report();
    await journal(entry);
  };

  let failure: unknown = null;
  const worker = async (): Promise<void> => {
    for (let n = queue.shift(); n !== undefined && failure === null; n = queue.shift()) {
      await sendPart(n).catch((e: unknown) => {
        failure ??= e;
      });
    }
  };
  report();
  await Promise.all(Array.from({ length: Math.min(UPLOAD_CONCURRENCY, queue.length) }, worker));
  if (failure !== null) throw failure;
  return Array.from({ length: count }, (_, i) => upload.etags[i + 1]);
}

// No object exists until the client completes; the R2 event that raises is the durable path.
async function sendTake(
  entry: PendingUpload,
  blob: Blob,
  upload: Upload,
  onProgress?: UploadProgress,
  onServerState?: (state: UploadServerState) => void,
  restarted = false,
): Promise<TakeUpload> {
  const { recordingId, takeId, reason, contentType, sizeBytes } = entry;
  const id = bareRecordingId(recordingId);
  const { signal } = upload.controller;
  let stored = false;
  try {
    if (!entry.multipart) {
      const issue: IssueUploadUrlCommand = {
        id,
        contentType,
        sizeBytes,
        ...(reason !== 'Review' ? { reason } : {}),
      };
      const grant = await writing(
        upload,
        throughMove(signal, () =>
          api.post<IssueRecordingUploadResponse>(`/recordings/${id}/upload-url`, issue, { requiresAuth: true }),
        ),
      );
      entry.multipart = {
        objectKey: grant.objectKey,
        uploadId: grant.uploadId,
        partSizeBytes: grant.partSizeBytes,
        etags: {},
      };
      signal.throwIfAborted();
      await journal(entry);
    }
    const { objectKey, uploadId } = entry.multipart;
    const partETags = await sendParts(id, entry, blob, signal, onProgress);
    const complete: CompleteRecordingUploadRequest = { objectKey, uploadId, partETags };
    // A failed complete is a failed save; the journal keeps the entry for the resume pass.
    const answer = await withRetries(signal, () =>
      api.post<RecordingUploadCompletionResponse>(`/recordings/${id}/upload-complete`, complete, {
        requiresAuth: true,
        signal,
      }),
    );
    stored = answer?.state === RecordingUploadState.Recorded;
  } catch (e) {
    // 409: the bytes were stored before this call.
    if (e instanceof ApiError && e.status === 409) {
      await resolveConfirmedUpload(id, takeId);
      return { recordingId: id, alreadyStored: true, stored: true, confirmed: Promise.resolve(true) };
    }
    if (e instanceof ApiError && e.status === 404) await clearPending(id);
    if (e instanceof UploadGone && !restarted) {
      entry.multipart = undefined;
      await journal(entry);
      return sendTake(entry, blob, upload, onProgress, onServerState, true);
    }
    throw e;
  }

  const confirmed = awaitUploadConfirmed(id, signal, onServerState).then(async (ok) => {
    if (ok) await resolveConfirmedUpload(id, takeId);
    return ok;
  });
  // Not every caller awaits it, and a Discard rejects it.
  confirmed.catch(() => undefined);
  return { recordingId: id, alreadyStored: false, stored, confirmed };
}

export async function uploadThumbnail(
  recordingId: string,
  image: Blob,
  contentType: string,
): Promise<void> {
  const id = bareRecordingId(recordingId);
  const { uploadUrl, objectKey } = await api.post<IssueUploadUrlResponse>(
    `/recordings/${id}/thumbnail-url`,
    { id, contentType },
    { requiresAuth: true },
  );
  await putToSignedUrl(uploadUrl, image, contentType);
  const command: CompleteThumbnailUploadCommand = { id, objectKey, contentType };
  await api.post<Record<string, never>>(
    `/recordings/${id}/thumbnail-complete`,
    command,
    { requiresAuth: true },
  );
}

export async function uploadThumbnailBestEffort(
  recordingId: string,
  blob: Blob,
  existing?: Blob | null,
): Promise<void> {
  try {
    const image = existing ?? (await autoCaptureThumbnail(blob));
    if (image) await uploadThumbnail(recordingId, image, image.type || THUMBNAIL_CONTENT_TYPE);
  } catch (err) {
    console.warn('uploads: thumbnail upload failed', err);
  }
}

export async function requestReview(
  recordingId: string,
  reviewId: string,
  review: ReviewQualityOptions = {},
): Promise<void> {
  const id = bareRecordingId(recordingId);
  const command: RequestRecordingReviewCommand = {
    id,
    reviewId: AiReviewId(reviewId),
    ...(review.modelKey ? { modelKey: review.modelKey } : {}),
    ...(review.frame ? { frame: review.frame } : {}),
    ...(review.frameworks && review.frameworks.length > 0 ? { frameworks: review.frameworks } : {}),
    ...(review.pronunciation ? { pronunciation: review.pronunciation } : {}),
    ...(review.effort ? { effort: review.effort } : {}),
  };
  await api.post<void>(`/recordings/${id}/review`, command, { requiresAuth: true });
}

// An over-quota upload never confirms; stop re-PUTting it well before the 7-day blob TTL.
export const RESUME_MAX_AGE_MS = 24 * 60 * 60 * 1000;

export async function resumePendingUploads(uploadedIds: Set<string>): Promise<void> {
  let pending: PendingUpload[];
  try {
    pending = await localRecordingCache.listPending();
  } catch {
    return;
  }

  await Promise.allSettled(
    pending.map(async (entry) => {
      const { recordingId, takeId, createdAt } = entry;
      if (uploadedIds.has(recordingId)) return resolveConfirmedUpload(recordingId, takeId);
      if (sendingIds.has(recordingId)) return;
      sendingIds.add(recordingId);
      try {
        if (Date.now() - createdAt > RESUME_MAX_AGE_MS) return await clearPending(recordingId);
        const cached = localRecordingCache.isAvailable() ? await localRecordingCache.get(takeId) : null;
        if (!cached?.blob) return await clearPending(recordingId);
        const { saveFirst, ...saved } = entry;
        const upload = track(takeId, !saveFirst);
        if (saveFirst) {
          await writing(upload, saveThroughMove(saved, saveFirst, upload));
          await journal(saved);
        }
        // Hold the slot until confirmed: an upload still under way must not be re-PUT.
        await (await sendTake(saved, cached.blob, upload)).confirmed;
      } finally {
        sendingIds.delete(recordingId);
      }
    }),
  );
}
